import React, { Fragment, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { asideLinks2 } from "../../constants/asideLinks2";
import { useTranslation } from "react-i18next";
import { DIRECTION, translate } from "services/general";
import {
  HAS_PERMISSION,
  getAsideLinkPermissionId,
  hasAsideLinkPermission,
} from "services/user";
import { DownArrow } from "components/shared/icons";
import MaskIcon from "components/shared/MaskIcon";
import PermissionDebugger from "components/shared/PermissionDebugger";
import { getAppVersion } from "utils/version";

const SideBar = ({ isDark }) => {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const [activeChild, setActiveChild] = useState({});
  const [appVersion, setAppVersion] = useState("1.0.1");
  let aside = useRef();

  // Helper: check if menu path matches current pathname and query params
  const isPathActive = (menuPath, currentPathname) => {
    if (!menuPath) return false;
    const [basePath, menuQuery] = menuPath.split("?");

    if (menuQuery) {
      const currentParams = new URLSearchParams(search);
      const menuParams = new URLSearchParams(menuQuery);
      for (const [key, value] of menuParams.entries()) {
        if (currentParams.get(key) !== value) return false;
      }
      return basePath === currentPathname;
    }

    // No query params in menu - check path match or dynamic route
    if (basePath === currentPathname) return true;
    if (currentPathname.startsWith(basePath + "/")) return true;
    return false;
  };

  // Check if any nested children are active
  const isNestedChildActive = (children, pathname) => {
    if (!children) return false;
    return children.some((child) => {
      if (isPathActive(child.path, pathname)) return true;
      if (child.children) {
        return isNestedChildActive(child.children, pathname);
      }
      return false;
    });
  };

  // Check if parent should be active
  const isParentActive = (path, children, pathname) => {
    if (path && isPathActive(path, pathname)) return true;
    return isNestedChildActive(children, pathname);
  };

  // Toggle child dropdown
  const toggleChild = (parentIndex, childIndex) => {
    setActiveChild((prev) => ({
      ...prev,
      [`${parentIndex}-${childIndex}`]: !prev[`${parentIndex}-${childIndex}`],
    }));
  };

  // Shows the permission id on hover only when the entry names one, so a row does not
  // have to be written twice just to be wrapped.
  const withPermissionDebugger = (permissionId, node) =>
    permissionId ? (
      <PermissionDebugger permissionId={permissionId}>
        {node}
      </PermissionDebugger>
    ) : (
      node
    );

  // Render nested children recursively
  const renderNestedChildren = (children, parentIndex, level = 1) => {
    if (!children) return null;

    return children.map((child, childIndex) => {
      const hasNestedChildren = child.children && child.children.length > 0;
      const childKey = `${parentIndex}-${childIndex}`;
      const isChildExpanded = activeChild[childKey];
      const isDirectChildActive = isPathActive(child.path, pathname);
      const hasActiveNestedChild = isNestedChildActive(
        child.children,
        pathname
      );
      const isChildActive = isDirectChildActive || hasActiveNestedChild;
      const shouldShowNested = isChildExpanded || isChildActive;
      const hasPermission = hasAsideLinkPermission(child);
      // A group is shown to anyone who may open one of its children, even when the group
      // itself names a permission they do not hold — otherwise nesting a page under a
      // heading would hide it from the people it belongs to.
      const hasAnyGrandchildPermission = child.children?.some((grandchild) =>
        hasAsideLinkPermission(grandchild)
      );

      if (!hasPermission && !hasAnyGrandchildPermission) return null;

      // Physical `marginLeft` on purpose. The logical property looks like the correct
      // fix, but it resolves against the direction in force here and lands on the right
      // edge in Arabic — which is the side the icons already sit on, so the rows shift
      // out from under the group heading instead of stepping in beneath it.
      const indent = { marginLeft: `${level * 16}px` };
      const label = translate(t(child.text), t(child.en_text));

      const row = hasNestedChildren ? (
        <div>
          <div
            className={`flex justify-between items-center cursor-pointer duration-200 py-[6.5px] rounded-md ${
              isChildActive ? "font-bold" : ""
            }`}
            style={indent}
            onClick={() => toggleChild(parentIndex, childIndex)}
          >
            <div className="flex items-center gap-[10px]">
              <div
                className={`relative bottom-0.5 ${
                  isDark ? "invert brightness-0" : ""
                }`}
              >
                {child.icon}
              </div>
              <span>{label}</span>
            </div>
            <DownArrow
              isDark={isDark}
              className={`${!shouldShowNested ? "rotate-0" : "rotate-180"} `}
            />
          </div>
          <div
            className={`transition-all duration-300 ease-in-out overflow-hidden ${
              shouldShowNested ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <div className="mt-1">
              {renderNestedChildren(child.children, parentIndex, level + 1)}
            </div>
          </div>
        </div>
      ) : (
        <Link
          to={child.path}
          className={`overflow-hidden py-[6.5px] rounded-md duration-200 flex items-center gap-[10px] ${
            isDirectChildActive ? "font-bold" : ""
          }`}
          style={indent}
        >
          <div
            className={`relative bottom-1 ${isDark ? "invert brightness-0" : ""}`}
          >
            {child.icon}
          </div>
          {label}
        </Link>
      );

      return (
        <Fragment key={childKey}>
          <div className="mb-1">
            {withPermissionDebugger(getAsideLinkPermissionId(child), row)}
          </div>
        </Fragment>
      );
    });
  };

  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, []);

  // Fetch app version
  useEffect(() => {
    getAppVersion().then((version) => {
      setAppVersion(version);
    });
  }, []);

  // Initialize expanded state for items with active nested children
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let last = localStorage.getItem("user");
    const id = setInterval(() => {
      const now = localStorage.getItem("user");
      if (now !== last) {
        last = now;
        setRefreshKey((k) => k + 1);
      }
    }, 300);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const newActiveChild = {};
    asideLinks2.forEach((link, parentIndex) => {
      if (link.children) {
        link.children.forEach((child, childIndex) => {
          // Check if direct child or any nested child is active
          const isDirectActive = isPathActive(child.path, pathname);
          const hasActiveNested = isNestedChildActive(child.children, pathname);
          if (isDirectActive || hasActiveNested) {
            newActiveChild[`${parentIndex}-${childIndex}`] = true;
          }
        });
      }
    });
    setActiveChild(newActiveChild);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, refreshKey]); // ✅ add refreshKey

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  let [isOpen, setIsOpen] = useState(false);

  // Close the mobile drawer whenever navigation lands on a new module.
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  return (
    <>
      <button
        className={`w-10 h-10 lg:hidden shadow-lg rounded-xl bg-slate-300 absolute top-[100px] cursor-pointer flex items-center justify-center z-[60] ${
          isOpen ? "-end-0" : "-start-4"
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <MaskIcon
          src="/icons/arrow-right.svg"
          alt="arrow"
          className={`h-[15px] w-[17px] bg-primary ${
            isOpen ? "" : "rotate-180"
          } transition-transform duration-300`}
        />
      </button>
      <aside
        className={`
         fixed start-0 
         ${isScrolled ? "top-0 h-screen" : "top-16 sm:top-20 h-[calc(100vh-64px)] sm:h-[calc(100vh-80px)]"}
         overflow-y-auto lg:w-[241px] w-full ps-[13px] duration-500 bar z-[50] 
       ${isDark ? "bg-chrome text-white" : "bg-surface text-secondary"} ${
         DIRECTION === "ltr" ? "pe-[20px]" : ""
       } ${isOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0"}`}
        ref={aside}
      >
        <ul className="mt-2">
          {asideLinks2.map(({ en_text, text, path, icon, children, id }, i) => {
            const shouldParentBeActive = isParentActive(
              path,
              children,
              pathname
            );

            return !children ? (
              <Fragment key={`aside-${path}`}>
                {HAS_PERMISSION(id) ? (
                  <PermissionDebugger permissionId={id}>
                    <Link to={path} key={path}>
                      <li
                        className={`flex gap-[10px] pb-[6.5px] px-[10px] rounded-[5px] items-center duration-200 cursor-pointer   ${
                          isPathActive(path, pathname) ? "font-bold" : ""
                        } ${i !== 0 && "mt-[16px]"}`}
                      >
                        <div
                          className={`relative bottom-0.5 ${
                            isDark ? "invert brightness-0" : ""
                          }`}
                        >
                          {icon}
                        </div>
                        {t(translate(text, en_text))}
                      </li>
                    </Link>
                  </PermissionDebugger>
                ) : (
                  ""
                )}
              </Fragment>
            ) : (
              <section key={path || text}>
                {children.some(
                  (child) =>
                    hasAsideLinkPermission(child) ||
                    child.children?.some((sub) => hasAsideLinkPermission(sub))
                ) && (
                  <>
                    {/* Parent header - no click handler, no arrow */}
                    <div
                      className={`pb-[14px] pt-[35px] px-[8px] rounded-[5px] ${
                        shouldParentBeActive ? "font-bold " : "font-normal"
                      }`}
                    >
                      <li className="flex gap-[10px] font-black ">
                        {t(translate(text, en_text))}
                      </li>
                    </div>

                    {/* Always show first-level children */}
                    <div className="text-sm font-medium cursor-pointer">
                      <div className="overflow-hidden pb-2">
                        {renderNestedChildren(children, i)}
                      </div>
                    </div>
                  </>
                )}
              </section>
            );
          })}
        </ul>
        {/* Version info at the bottom */}
        <div
          className={`pt-4 pb-4 ${DIRECTION === "rtl" ? "ps-4 pe-[29px]" : "px-4"}`}
        >
          {/* Separator line */}
          <div
            className={`w-full h-px mb-6 ${isDark ? "bg-surface/20" : "bg-[#455560]/20"}`}
          ></div>
          <div className="flex flex-col items-center">
            <span
              className={`text-sm text-center ${isDark ? "text-white/60" : "text-secondary/60"}`}
            >
              {DIRECTION === "rtl"
                ? `إصدار رقم ${appVersion}`
                : `Version ${appVersion}`}
            </span>
          </div>
        </div>
      </aside>
    </>
  );
};

export default SideBar;
